
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, MapPin, Heart, TrendingUp, Star, Map, Crown, AlertCircle, Globe, Navigation, LocateFixed } from 'lucide-react';
import useBrazilianStates from '../../hooks/useBrazilianStates.js';
import { useAppContext } from '../../App.jsx';
import { GeoPoint, collection, query, where, getDocs, limit, startAfter } from "firebase/firestore";
import { USERS_COLLECTION, CHATS_COLLECTION } from '../../config/constants';
import ServiCard from '../../components/client/ServiCard';
import ServiDetailModal from '../../components/client/ServiDetailModal.jsx';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import TerviLogo from '../../components/common/TerviLogo.jsx';
import useDebounce from '../../hooks/useDebounce.js';

const ITEMS_PER_PAGE = 12;

const normalize = (str) => str ? str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() : "";

function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    if ([lat1, lon1, lat2, lon2].some(coord => coord === null || coord === undefined)) return Infinity;
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

const ClientDashboard = ({ onOpenChat }) => {
    const { user, db, getOrCreateChat, fullUserData, showMessage } = useAppContext();
    const [serviList, setServiList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedServi, setSelectedServi] = useState({ data: null, existingChatId: null });
    const [categorySearch, setCategorySearch] = useState('');
    const debouncedCategorySearch = useDebounce(categorySearch, 500);
    const { states, cities, loading: loadingCities, fetchCitiesForState } = useBrazilianStates();
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [clientLocation, setClientLocation] = useState(null);
    const [isGPSActive, setIsRealGPSActive] = useState(false);
    const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
    const [sortBy, setSortBy] = useState('relevancia');

    const [lastVisible, setLastVisible] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const listContainerRef = useRef(null);
    const stableFetchCities = useCallback(fetchCitiesForState, []);
    const locationFetchedRef = useRef(false);

    const fetchServis = useCallback(async (loadMore = false) => {
        if (!db || !user) return;
        if (loadMore && !hasMore) return;

        if (loadMore) setIsLoadingMore(true);
        else {
            setIsLoading(true);
            setServiList([]);
            setLastVisible(null);
        }

        try {
            const q = query(collection(db, USERS_COLLECTION), where("roles.servi", "==", true), limit(ITEMS_PER_PAGE));
            const snap = await getDocs(loadMore && lastVisible ? query(q, startAfter(lastVisible)) : q);
            const newServis = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            if (snap.docs.length > 0) setLastVisible(snap.docs[snap.docs.length - 1]);
            setServiList(prev => loadMore ? [...prev, ...newServis] : newServis);
            setHasMore(newServis.length === ITEMS_PER_PAGE);
        } catch (error) { console.error("Erro busca:", error); }
        finally { setIsLoading(false); setIsLoadingMore(false); }
    }, [db, user, lastVisible, hasMore]);

    useEffect(() => { fetchServis(false); }, [db, user]);

    const handleRequestGPS = useCallback(() => {
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            setClientLocation({ latitude, longitude });
            setIsRealGPSActive(true);

            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                const data = await response.json();
                if (data && data.address) {
                    const stateInfo = states.find(s => normalize(s.nome) === normalize(data.address.state));
                    if (stateInfo) {
                        setSelectedState(stateInfo.sigla);
                        await stableFetchCities(stateInfo.sigla);
                        const detectedCity = data.address.city || data.address.town || data.address.village;
                        setSelectedCity(detectedCity || "");
                    }
                }
            } catch (e) { console.error("Erro geocoding:", e); }
        }, () => {
            console.log("GPS recusado ou falhou.");
        }, { timeout: 10000, enableHighAccuracy: true });
    }, [states, stableFetchCities]);

    useEffect(() => {
        if (states.length > 0 && !locationFetchedRef.current) {
            locationFetchedRef.current = true;
            handleRequestGPS();
        }
    }, [states, handleRequestGPS]);

    useEffect(() => {
        if (!selectedCity || !selectedState || isGPSActive) return;

        const updateReferenceCoords = async () => {
            const stateName = states.find(s => s.sigla === selectedState)?.nome;
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(`${selectedCity}, ${stateName}, Brasil`)}&limit=1`);
                const data = await response.json();
                if (data && data.length > 0) {
                    setClientLocation({ latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) });
                }
            } catch (e) { console.error(e); }
        };
        updateReferenceCoords();
    }, [selectedCity, selectedState, states, isGPSActive]);

    useEffect(() => {
        if (selectedState) stableFetchCities(selectedState);
        else { setSelectedCity(''); setClientLocation(null); }
    }, [selectedState, stableFetchCities]);

    const handleShowDetails = async (serviData) => {
        if (!db || !user || !serviData) return;
        try {
            const snap = await getDocs(query(collection(db, CHATS_COLLECTION), where("participants", "array-contains", user.uid)));
            let activeId = null;
            const terminal = ['pago', 'finalizado', 'cancelado', 'recusado', 'concluído'];
            snap.forEach(doc => {
                const d = doc.data();
                if (d.serviId === serviData.id && !terminal.includes((d.status || '').toLowerCase())) activeId = doc.id;
            });
            setSelectedServi({ data: serviData, existingChatId: activeId });
        } catch (e) { setSelectedServi({ data: serviData, existingChatId: null }); }
    };

    const handleStartOrOpenChat = async (serviId, serviceTitle) => {
        if (!getOrCreateChat) return;
        const chatId = await getOrCreateChat(serviId, serviceTitle || 'Serviço Geral');
        if (chatId) onOpenChat(chatId);
        setSelectedServi({ data: null, existingChatId: null });
    };

    const processedList = useMemo(() => {
        const normSearch = normalize(debouncedCategorySearch);
        const normCity = normalize(selectedCity);
        const normState = normalize(selectedState);

        return serviList
        .filter(servi => servi.id !== user.uid)
        .filter(servi => {
            const profile = servi.profile_servi || {};
            const matchesCategory = normSearch === '' || normalize(profile.name).includes(normSearch) || normalize(profile.companyName).includes(normSearch) || normalize(profile.mainCategory).includes(normSearch) || (profile.additionalServices || []).some(s => normalize(s).includes(normSearch));
            const matchesFavorites = !showOnlyFavorites || (fullUserData?.profile_client?.favoriteServis || []).includes(servi.id);

            const matchesLocation = () => {
                if (profile.serviceScope === 'nacional') return true;
                const regions = (profile.regionsAttended || []);
                const clientStateFullName = normalize(states.find(s => s.sigla === selectedState)?.nome || "");

                if (profile.serviceScope === 'estadual') {
                    return regions.some(r => {
                        const name = normalize(typeof r === 'object' ? r.name : r);
                        const stateSigla = typeof r === 'object' ? normalize(r.state || "") : "";
                        return name.includes(clientStateFullName) || name.includes(normState) || (stateSigla && stateSigla === normState);
                    });
                }

                if (profile.serviceScope === 'local' || profile.serviceScope === 'bairro') {
                    if (clientLocation) {
                        return regions.some(r => {
                            if (typeof r === 'object' && r.location && r.radius > 0) {
                                const d = getDistanceFromLatLonInKm(clientLocation.latitude, clientLocation.longitude, r.location.latitude, r.location.longitude);
                                return d <= r.radius;
                            }
                            const name = normalize(typeof r === 'object' ? r.name : r);
                            return selectedCity ? name.includes(normCity) : (name.includes(clientStateFullName) || name.includes(normState));
                        });
                    } else {
                        return regions.some(r => {
                            const name = normalize(typeof r === 'object' ? r.name : r);
                            if (selectedCity) return name.includes(normCity);
                            return name.includes(clientStateFullName) || name.includes(normState);
                        });
                    }
                }
                if (!selectedState) return true;
                return false;
            };

            return matchesCategory && matchesLocation() && matchesFavorites;
        })
        .map(servi => {
            const profile = servi.profile_servi || {};
            let score = 0;
            let dist = Infinity;

            if (clientLocation && profile.regionsAttended) {
                const distances = profile.regionsAttended.map(region => {
                    const rObj = typeof region === 'object' ? region : {};
                    return rObj.location ? getDistanceFromLatLonInKm(clientLocation.latitude, clientLocation.longitude, rObj.location.latitude, rObj.location.longitude) : Infinity;
                });
                dist = Math.min(...distances);
            }

            if (profile.serviceScope === 'bairro') score += 70;
            else if (profile.serviceScope === 'local') score += 50;
            else if (profile.serviceScope === 'estadual') score += 20;

            if (dist < 100) score += (100 - dist) * 0.6;
            score += (profile.rating || 0) * 20;
            score += Math.min((profile.ratingCount || 0) * 0.2, 40);
            if (profile.isFeatured) score += 2000 + (profile.adPriority || 0);

            return { ...servi, relevanceScore: score, distance: dist };
        })
        .sort((a, b) => {
            if (sortBy === 'proximidade') return a.distance - b.distance;
            if (sortBy === 'avaliacao') {
                const rA = a.profile_servi?.rating || 0;
                const rB = b.profile_servi?.rating || 0;
                if (rA !== rB) return rB - rA;
                return (b.profile_servi?.ratingCount || 0) - (a.profile_servi?.ratingCount || 0);
            }
            return b.relevanceScore - a.relevanceScore;
        });
    }, [serviList, debouncedCategorySearch, showOnlyFavorites, selectedState, selectedCity, clientLocation, sortBy, states, fullUserData, user]);

    if (isLoading) return <AuthLoadingScreen message="Buscando profissionais..." />;

    const locationDisplay = selectedCity ? `${selectedCity}, ${selectedState}` : (selectedState ? states.find(s => s.sigla === selectedState)?.nome : 'Brasil');

    return (
        <div className="h-full flex flex-col bg-gray-100 overflow-hidden">
            <header className="bg-white shadow-lg p-3 z-10 text-left shrink-0">
                <div className="flex justify-between items-center">
                    <div><TerviLogo size={28} /></div>
                    <div className="flex items-center text-right">
                        <div className="max-w-[150px]">
                            <h2 className="font-bold text-gray-800 text-sm truncate">{fullUserData?.profile_client?.name || 'Cliente'}</h2>
                            <p className="text-[10px] text-gray-500 flex items-center justify-end">
                                {isGPSActive ? <Navigation className="w-2.5 h-2.5 mr-1 text-blue-600" /> : <MapPin className="w-2.5 h-2.5 mr-1" />}
                                {locationDisplay}
                            </p>
                        </div>
                        <div className="w-9 h-9 rounded-xl overflow-hidden bg-white border border-blue-100 shadow-sm flex items-center justify-center p-0.5 ml-3 flex-shrink-0">
                            <img src={fullUserData?.profile_client?.photoUrl || `https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff`} className="w-full h-full object-contain rounded-lg" alt="User"/>
                        </div>
                    </div>
                </div>
            </header>

            <div ref={listContainerRef} onScroll={e => {
                const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                if (scrollHeight - scrollTop < clientHeight * 1.5 && hasMore && !isLoadingMore) fetchServis(true);
            }} className="flex-grow overflow-y-auto p-4 pb-24 text-left text-sm no-scrollbar">

                 <div className="space-y-4 mb-4">
                    <div className="relative">
                        <input
                            id="service-search"
                            name="service-search"
                            autoComplete="off"
                            placeholder="Busque por serviço, nome..."
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-transparent bg-white shadow-sm focus:border-blue-500 transition-all outline-none font-semibold text-sm"
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 text-gray-400" />
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleRequestGPS}
                            className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center shadow-sm ${isGPSActive ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-100 text-gray-400 hover:text-blue-500'}`}
                            title="Usar GPS"
                        >
                            <LocateFixed size={18} className={isGPSActive ? 'animate-pulse' : ''} />
                        </button>

                        <div className="flex-1 grid grid-cols-2 gap-2">
                            <select value={selectedState} onChange={e => { setIsRealGPSActive(false); setSelectedState(e.target.value); setSelectedCity(''); stableFetchCities(e.target.value); }} className="w-full p-3 border-2 border-transparent bg-white rounded-xl font-bold text-gray-700 outline-none focus:border-blue-500 shadow-sm appearance-none text-xs"><option value="">Brasil</option>{states.map(s => <option key={s.sigla} value={s.sigla}>{s.nome}</option>)}</select>
                            <select value={selectedCity} onChange={e => { setIsRealGPSActive(false); setSelectedCity(e.target.value); }} disabled={!selectedState} className="w-full p-3 border-2 border-transparent bg-white rounded-xl font-bold text-gray-700 outline-none focus:border-blue-500 shadow-sm appearance-none text-xs disabled:opacity-50"><option value="">Cidade</option>{cities.map(c => <option key={c} value={c}>{c}</option>)}</select>
                        </div>
                    </div>
                 </div>

                {/* CORREÇÃO CIRÚRGICA: Container de filtros com scroll horizontal suave e sem cortes */}
                <div className="w-full overflow-x-auto no-scrollbar border-y py-3 mb-4 bg-gray-50/50">
                    <div className="flex items-center space-x-2 px-1 min-w-max">
                        <button onClick={() => setSortBy('relevancia')} className={`flex items-center px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${sortBy === 'relevancia' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-gray-500 border border-gray-100'}`}>
                            <TrendingUp size={14} className="mr-1.5"/> Relevância
                        </button>
                        <button onClick={() => setSortBy('avaliacao')} className={`flex items-center px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${sortBy === 'avaliacao' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-gray-500 border border-gray-100'}`}>
                            <Star size={14} className="mr-1.5"/> Avaliações
                        </button>
                        <button onClick={() => setSortBy('proximidade')} className={`flex items-center px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${sortBy === 'proximidade' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-gray-500 border border-gray-100'}`}>
                            <Map size={14} className="mr-1.5"/> Proximidade
                        </button>
                        <div className="h-6 w-px bg-gray-200 mx-1"></div>
                        <button onClick={() => setShowOnlyFavorites(!showOnlyFavorites)} className={`flex items-center px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${showOnlyFavorites ? 'bg-red-500 text-white shadow-lg' : 'bg-white text-gray-500 border border-gray-100'}`}>
                            <Heart size={14} className={`mr-1.5 ${showOnlyFavorites ? 'fill-current' : ''}`} /> Favoritos
                        </button>
                    </div>
                </div>

                <div className="space-y-3">
                    {processedList.length > 0 ? (
                        processedList.map(servi => <ServiCard key={servi.id} servi={{...servi.profile_servi, id: servi.id, distance: servi.distance}} onShowDetails={() => handleShowDetails(servi)} />)
                    ) : (
                        <div className="text-center py-16 bg-white rounded-[2.5rem] border-2 border-dashed border-gray-100 px-6">
                            <AlertCircle size={40} className="text-gray-200 mx-auto mb-3" />
                            <p className="text-gray-500 font-bold uppercase tracking-widest text-sm text-center">Filtro muito restrito</p>
                            <p className="text-[10px] text-gray-400 mt-1 leading-relaxed text-center">Não encontramos profissionais nesta região.</p>
                            <button onClick={() => { setSelectedState(''); setSelectedCity(''); setClientLocation(null); setIsRealGPSActive(false); }} className="mt-4 flex items-center justify-center mx-auto bg-blue-50 text-blue-600 px-4 py-2 rounded-xl text-xs font-bold hover:bg-blue-100 transition-all">
                                <Globe size={14} className="mr-2" /> Ver todos do Brasil
                            </button>
                        </div>
                    )}
                    {isLoadingMore && <div className="text-center p-4 text-gray-500 italic">Carregando mais profissionais...</div>}
                </div>
            </div>

            {selectedServi.data && <ServiDetailModal servi={selectedServi.data} fullUserData={fullUserData} existingChatId={selectedServi.existingChatId} onClose={() => setSelectedServi({ data: null, existingChatId: null })} onStartChat={(title) => handleStartOrOpenChat(selectedServi.data.id, title)} onOpenChat={() => handleStartOrOpenChat(selectedServi.data.id)} />}
        </div>
    );
};

export default ClientDashboard;
